import { useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconTrash } from '@components/icons/trash';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useDeleteJobLabel } from 'api/labels/hooks';
import { ModalProps } from 'features/modals';

type Props = ModalProps<{
  labelId: number;
}>;

export const DeleteJobLabelModal = (props: Props) => {
  const { t } = useScopedTranslate(locale);
  const [confirm, setConfirm] = useState(false);
  const onSuccess = async () => {
    props.closeModal();
  };
  const deleteMutation = useDeleteJobLabel({ id: props.labelId, onSuccess });

  return (
    <Stack space={6}>
      <Text font="heading1" text={t('deleteLabel')} />
      <Stack>
        <Text text={t('areYouSure')} />
        <Text color="red100" text={t('cannotBeUndone')} />
      </Stack>
      <Separator />
      <Stack space={4}>
        <Text font="semibold14" text={t('pleaseConfirm')} />
        <Stack space={2} orientation="horizontal">
          <Checkbox label="" labelVisible={false} onChange={(value) => setConfirm(value)} />
          <Text text={t('iUnderstand')} />
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button variant="secondary" onClick={props.closeModal} text={t('cancel')} />
        <Button
          isDisabled={!confirm}
          onClick={() => deleteMutation.mutate()}
          variant="primary-red"
          iconLeft={<IconTrash />}
          text={t('delete')}
        />
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    deleteLabel: 'Delete job label',
    areYouSure:
      'Are you sure you want to delete this label? \nIt will be removed from all jobs it is currently applied to',
    cannotBeUndone: 'This cannot be undone',
    cancel: 'Cancel',
    delete: 'Delete',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand',
  },
  is: {
    deleteLabel: 'Eyða merki starfs',
    areYouSure:
      'Ertu viss um að þú viljir eyða þessu merki? \nÞað verður fjarlægt af öllum störfum sem það er skráð á',
    cannotBeUndone: 'Þetta er ekki hægt að afturkalla',
    cancel: 'Hætta við',
    delete: 'Eyða',
    pleaseConfirm: 'Vinsamlegast staðfestu',
    iUnderstand: 'Ég staðfesti',
  },
};
